import { VwPluralPipe } from '../@commons/pipes/vw-plural.pipe';
import { Component, OnInit, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from '../@commons/ui-components/shared.module';
import { fadeIn } from '../@commons/animations/animations';
import { HeaderPaddingSafeAreaComponent } from "../@commons/@components/header-padding-safe-area/header-padding-safe-area.component";
import { CalendarCultivationHierarchyRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/cultivation/calendar-cultivation-hierarchy-list.request';
import { CalendarCultivationListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/calendar-cultivation-list.response';
import { CalendarCultivationHierarchyListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/calendar-cultivation-hierarchy-list.response';
import { CultivationVariantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/cultivation-variant-list.response';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { CultivationHierarchyApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-hierarchy/cultivation-hierarchy-api';
import { StorageService } from '../@commons/@services/front/storage.service';
import { CalendarHierarchiesComponent } from './@components/calendar-hierarchies/calendar-hierarchies.component';
import { CultivationGuideToastComponent } from '../garden/@toasts/cultivation-guide-toast/cultivation-guide-toast.component';
import { CultivationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-api';
import { CultivationResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/cultivation.response';
import { CultivationVariantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-variant/cultivation-variant-api';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { PlantIconComponent } from '../@icons/plant-icon.component';
import { ClimateBannerComponent } from './@components/climate-banner/climate-banner.component';
import { ClimateInfoToastComponent } from './@toasts/climate-info-toast/climate-info-toast.component';
import { ClimatePickerToastComponent, ClimateOption } from './@toasts/climate-picker-toast/climate-picker-toast.component';
import { ClimateApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/climate/climate.api';
import { WeatherApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/weather/weather-api';
import { AddressApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address/address-api';
import { LocalFrostDatesResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/local-frost-dates.response';
import { CalendarPeriodLegendComponent } from './@components/calendar-period-legend/calendar-period-legend.component';
import { FrostDatesToastComponent } from './@toasts/frost-dates-toast/frost-dates-toast.component';
import { CalendarBadge } from './@components/calendar-hierarchies/calendar-hierarchy-card/calendar-hierarchy-card-item/calendar-hierarchy-card-item.component';
import { CALENDAR_FILTERS, CalendarFilter, nextBusyMonth, rowsForMonth, rowsForSearch } from './calendar-rows';
import { dayPosition } from '../@commons/@services/front/period-bars.util';
import { ChoiceOption } from '../@commons/ui-components/choice-selector/choice-selector.component';
import { dayMonthName, monthName } from '../@commons/@services/front/seed-timing.util';
import { SafeAreaService } from '../@commons/@services/webview/safe-area.service';

/** Au-delà de ce déplacement horizontal, le doigt glisse sur l'axe : ce n'est plus un toucher. */
const SCRUB_THRESHOLD_PX = 4;

/**
 * Calendrier « ce mois-ci » (PC-11 b, maquette M8).
 *
 * En tête, le climat et les dates de gel de l'adresse (CJ-04). Sous l'en-tête
 * collant (recherche, filtres, axe des mois), la légende puis les cultures
 * qui ont quelque chose à faire dans le mois choisi, en A-Z, une ligne serrée
 * chacune (nom à gauche, barres à droite).
 *
 * Le mois se choisit sur l'axe des mois lui-même (retours 2) : les flèches
 * doublaient l'axe sans dire que le mois filtre la liste. Un seul trait au
 * jour d'aujourd'hui traverse toute la liste.
 * Une recherche montre toutes les cultures qui répondent, sans filtre de mois.
 */
import { EmptyStateComponent } from '../@commons/ui-components/empty-state/empty-state.component';
import { SearchIconComponent } from '../@icons/search-icon.component';
import { nearestClimateVariant } from '../@commons/utils/climate-variant.util';
import { GardenClimateStore } from '../@commons/@services/data/garden-climate.store';
@Component({
  selector: 'vw-calendar',
  imports: [
    VwPluralPipe,
    SharedModule,
    TranslatePipe,
    HeaderPaddingSafeAreaComponent,
    CalendarHierarchiesComponent,
    CultivationGuideToastComponent,
    PlantIconComponent,
    ClimateBannerComponent,
    ClimateInfoToastComponent,
    ClimatePickerToastComponent,
    CalendarPeriodLegendComponent,
    FrostDatesToastComponent,
    EmptyStateComponent,
    SearchIconComponent,
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  animations: [fadeIn],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: CultivationHierarchyApi, useClass: CultivationHierarchyApi },
    { provide: PlantApi, useClass: PlantApi },
    { provide: CultivationApi, useClass: CultivationApi },
    { provide: CultivationVariantApi, useClass: CultivationVariantApi },
    { provide: ClimateApi, useClass: ClimateApi },
    { provide: WeatherApi, useClass: WeatherApi },
    { provide: AddressApi, useClass: AddressApi },
  ]
})
export class CalendarComponent implements OnInit {
  isLoading = false;
  /** Le calendrier n'a pas pu être chargé : jamais confondu avec « aucune plante ». */
  loadError = false;
  /** Toutes les cultures du climat ; le mois et le filtre se choisissent sans appel. */
  calendarItems: CalendarCultivationHierarchyListResponse[] = [];
  /** Résultats d'une recherche en cours ; nul hors recherche. */
  searchResults: CalendarCultivationHierarchyListResponse[] | null = null;
  searchValue = '';
  isShowingGuide = false;
  selectedCultivation: CultivationResponse;
  selectedVariant: CultivationVariantListResponse;

  /** Le mois en cours, de 1 à 12 : dit « mois en cours » au lecteur d'écran. */
  readonly currentMonth = new Date().getMonth() + 1;
  /** Mois choisi sur l'axe, de 1 à 12 : le mois en cours à l'ouverture. */
  month = this.currentMonth;
  /** Aujourd'hui sur l'axe, en % : le haut du trait qui traverse la liste. */
  readonly todayLeft = (dayPosition(new Date()) / 12) * 100;
  /** Le doigt glisse sur l'axe des mois : chaque mois survolé est choisi. */
  isScrubbing = false;
  private scrubStartX = 0;
  /** Le dernier geste sur l'axe a glissé : le clic qui le termine ne reprend pas la lettre de départ. */
  private scrubMoved = false;
  filter: CalendarFilter = 'all';
  readonly filterOptions: ChoiceOption[] = CALENDAR_FILTERS.map((value) => ({ value, label: 'calendar.filters.' + value }));
  readonly monthNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  // Climate state
  selectedClimateType: string | null = null;
  myClimateType: string | null = null;
  isClimateInfoOpen = false;
  isClimatePickerOpen = false;
  availableClimates: ClimateOption[] = [];

  /** Commune de l'adresse (« Grâce-Hollogne »). */
  city: string | null = null;
  frostDates: LocalFrostDatesResponse | null = null;
  isFrostSheetOpen = false;

  /** Cultures en stock (graines) et au potager, pour les badges des lignes. */
  private stockUids = new Set<string>();
  private gardenUids = new Set<string>();

  /** Hauteur à laquelle l'en-tête se colle : sous la bande de la barre d'état. */
  stickyTop = 64;

  private readonly destroyRef = inject(DestroyRef);
  private readonly gardenClimate = inject(GardenClimateStore);

  constructor(
    private cultivationHierarchyApi: CultivationHierarchyApi,
    private cultivationApi: CultivationApi,
    private cultivationVariantApi: CultivationVariantApi,
    private climateApi: ClimateApi,
    private weatherApi: WeatherApi,
    private addressApi: AddressApi,
    private storageService: StorageService,
    safeAreaService: SafeAreaService) {
    safeAreaService.safeAreaData$.pipe(takeUntilDestroyed()).subscribe((data) => {
      this.stickyTop = data.top || 64;
    });
  }

  async ngOnInit() {
    this.setupSearchDebounce();
    // Le calendrier d'abord ; le reste habille l'écran et ne bloque rien.
    await Promise.all([
      this.loadCalendar(),
      this.loadAvailableClimates(),
      this.loadMyClimate(),
      this.loadBadges(),
      this.loadFrostDates(),
      this.loadCity(),
    ]);
  }

  async loadAvailableClimates() {
    try {
      this.availableClimates = await this.climateApi.listAvailable();
    } catch (e) {
      // silent — climate picker is non-blocking
    }
  }

  /** Sans climat connu, le libellé générique « Ton climat ». */
  async loadMyClimate() {
    this.myClimateType = await this.gardenClimate.climate();
  }

  /** La commune, pour « Ton climat : océanique tempéré (Grâce-Hollogne) ». */
  async loadCity() {
    const addressUid = this.storageService.getItem('address-uid');
    if (!addressUid) return;
    try {
      const address = await this.addressApi.find(addressUid, this.storageService.getCurrentLanguage());
      this.city = (address as any)?.city || null;
    } catch (e) {
      console.warn('CalendarComponent: failed to load the address city', e);
    }
  }

  /**
   * Dates de gel de l'adresse (CJ-04). Sans assez d'historique, le serveur
   * répond sans date : la ligne se masque, jamais de date inventée.
   */
  async loadFrostDates() {
    if (!this.storageService.getItem('address-uid')) return;
    try {
      this.frostDates = await this.weatherApi.getFrostDates();
    } catch (e) {
      console.warn('CalendarComponent: failed to load frost dates', e);
    }
  }

  /**
   * « En stock » et « Au potager » : les cultures de ses graines et de son
   * potager, par la même lecture du calendrier que Mes graines. Un compte
   * sans graine ou sans plante reçoit un 400 : pas de badge, rien d'autre.
   */
  async loadBadges() {
    const addressUid = this.storageService.getItem('address-uid');
    if (!addressUid) return;
    const uidsOf = async (flag: 'mySeeds' | 'myGarden'): Promise<Set<string>> => {
      const request = new CalendarCultivationHierarchyRequest();
      request.addressUid = addressUid;
      request[flag] = true;
      try {
        const hierarchies = await this.cultivationHierarchyApi.calendar(request, this.storageService.getCurrentLanguage());
        return new Set((hierarchies ?? []).flatMap((h) => (h.cultivations ?? []).map((c) => c.uid)));
      } catch {
        return new Set();
      }
    };
    [this.stockUids, this.gardenUids] = await Promise.all([uidsOf('mySeeds'), uidsOf('myGarden')]);
  }

  /** Badge d'une culture : « Au potager » prime sur « En stock ». Fonction stable, passée aux lignes. */
  readonly badgeOf = (cultivationUid: string): CalendarBadge =>
    this.gardenUids.has(cultivationUid) ? 'garden' : this.stockUids.has(cultivationUid) ? 'stock' : null;

  private buildRequest(): CalendarCultivationHierarchyRequest {
    const calendarRequest = new CalendarCultivationHierarchyRequest();
    calendarRequest.addressUid = this.storageService.getItem('address-uid') ?? undefined;
    if (this.selectedClimateType) {
      (calendarRequest as any).climateType = this.selectedClimateType;
    }
    return calendarRequest;
  }

  async loadCalendar() {
    this.isLoading = true;
    try {
      this.calendarItems = await this.cultivationHierarchyApi.calendar(this.buildRequest(), this.storageService.getCurrentLanguage());
      this.loadError = false;
    } catch (e) {
      console.error('CalendarComponent: failed to load calendar', e);
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }

  onClimateSelected(type: string | null) {
    this.selectedClimateType = type;
    this.loadCalendar();
  }

  get isSearching(): boolean {
    return this.searchValue.trim().length > 0;
  }

  /** Les lignes affichées : le mois choisi et son filtre, ou les résultats de la recherche. */
  get rows(): CalendarCultivationHierarchyListResponse[] {
    const lang = this.storageService.getCurrentLanguage();
    if (this.isSearching) return rowsForSearch(this.searchResults ?? [], lang);
    return rowsForMonth(this.calendarItems, this.month, this.filter, lang);
  }

  /** Des lignes à montrer : l'axe des mois et la légende les accompagnent. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  /** Mois creux : on montre le prochain mois chargé. */
  get busyMonth(): { month: number; count: number } | null {
    return this.isLoading || this.loadError ? null : nextBusyMonth(this.calendarItems, this.month);
  }

  monthLabel(month: number): string {
    return monthName(month, this.storageService.getCurrentLanguage());
  }

  goToMonth(month: number) {
    this.month = month;
  }

  /**
   * Toucher une lettre de l'axe choisit son mois (le clic du bouton) ; glisser le
   * doigt le long de l'axe choisit chaque mois survolé : une lettre fait 16 px de
   * large, glisser rattrape un toucher imprécis. Un geste vertical reste un
   * défilement de la page (`touch-pan-y`) et ne change pas de mois.
   */
  onAxisPointerDown(event: PointerEvent) {
    if (this.isSearching) return;
    this.isScrubbing = true;
    this.scrubMoved = false;
    this.scrubStartX = event.clientX;
  }

  onAxisPointerMove(event: PointerEvent) {
    if (!this.isScrubbing || Math.abs(event.clientX - this.scrubStartX) <= SCRUB_THRESHOLD_PX) return;
    this.scrubMoved = true;
    this.pickMonthAt(event);
  }

  /** Le clic d'une lettre ; au clavier (`detail` à 0), toujours pris. */
  onAxisMonthClick(month: number, event: MouseEvent) {
    if (this.scrubMoved && event.detail > 0) return;
    this.month = month;
  }

  private pickMonthAt(event: PointerEvent) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const index = Math.floor(((event.clientX - rect.left) / rect.width) * 12);
    this.month = Math.min(12, Math.max(1, index + 1));
  }

  setFilter(filter: CalendarFilter) {
    this.filter = filter;
  }

  /** Dates de gel dans la langue de l'app (« 20 avril ») ; vide sans date. */
  frostDay(mmdd: string | null | undefined): string {
    return mmdd ? dayMonthName(mmdd, this.storageService.getCurrentLanguage()) : '';
  }

  /** La ligne de gel a quelque chose à dire, et parle de l'adresse (pas d'un climat exploré). */
  get hasFrostLine(): boolean {
    return !this.selectedClimateType && !!(this.frostDates?.lastSpringFrost || this.frostDates?.firstAutumnFrost);
  }

  get frostLineKey(): string {
    if (this.frostDates?.lastSpringFrost && this.frostDates?.firstAutumnFrost) return 'calendar.frost.line.both';
    return this.frostDates?.lastSpringFrost ? 'calendar.frost.line.last' : 'calendar.frost.line.first';
  }

  async onCultivationSelected(cultivation: CalendarCultivationListResponse) {
    const cultivationResponse = await this.cultivationApi.find(cultivation.uid, this.storageService.getCurrentLanguage());
    this.selectedCultivation = cultivationResponse;
    this.isShowingGuide = true;
    // La variante du climat affiché (choisi, sinon le sien), ou du plus proche.
    const variantResponse = await this.cultivationVariantApi.list({ cultivationUid: cultivation.uid }, 0, 10, this.storageService.getCurrentLanguage());
    this.selectedVariant = nearestClimateVariant(variantResponse.results, this.selectedClimateType ?? this.myClimateType)!;
  }

  private searchSubject = new Subject<string>();

  private setupSearchDebounce() {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((searchValue) => this.runSearch(searchValue));
  }

  private async runSearch(searchValue: string) {
    if (!searchValue.trim()) {
      this.searchResults = null;
      return;
    }
    this.isLoading = true;
    const calendarRequest = this.buildRequest();
    (calendarRequest as any).name = searchValue;
    try {
      this.searchResults = await this.cultivationHierarchyApi.calendar(
        calendarRequest,
        this.storageService.getCurrentLanguage()
      );
      this.loadError = false;
    } catch (e) {
      console.error('CalendarComponent: failed to search calendar', e);
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }

  onSearch(searchValue: string) {
    this.searchValue = searchValue ?? '';
    this.searchSubject.next(this.searchValue);
  }

  clearSearch() {
    this.onSearch('');
  }

  /** « Réessayer » : la recherche en cours, sinon le calendrier. */
  retry() {
    if (this.isSearching) {
      this.runSearch(this.searchValue);
    } else {
      this.loadCalendar();
    }
  }
}
